/*************************************** THIRD-PARTY IMPORTS **************************************/
import {observable, action, computed, IObservableArray} from 'mobx';
import localforage from 'localforage';
import {isDevelopment} from 'env-var-helpers';
import {get} from 'mad-utils/lib/react';
import {parse as qsParse} from 'query-string';

import io from 'socket.io-client';

/**************************************** PROJECT MODULES *****************************************/
import {BaseStore} from './base-store';
import {EnvVarStore} from './environment/env-var-store';
import {QueryParamStore} from './environment/query-param-store';
import {LanguageStore} from './user/language-store';
import {UIStore} from './ui/ui-store';
import {RecordModel} from './jvlns/record-model';
import {ModelInitStatusStore} from './app/model-init-status-store';
import {AccountStore} from './user/account-store';
import {RecordModelsStore} from './jvlns/record-models-store';

import {SupportedLang, setLoginCookie, config} from '@canimmunize/core-imports';

import {
    SyncManagerInstance,
    javelinDataStore,
    JavelinManagerInstance,
    NotificationManager,
    SchedulerManager,
    RecordInstance,
    Broadcaster,
    JavelinData,
    RequestCallback,
    JvlnsErrorInstance,
    SuccessInstance,
} from '@canimmunize/javelinscript';

/**************************************** TYPE DEFINITIONS ****************************************/
type _EnvVars = Record<string, string>;
type LangSetter = ((lang: SupportedLang) => void);

const RM_TAG = RecordModel.prototype.constructor.name;
const RM_REGEX = new RegExp(`^\[${RM_TAG}<[0-9a-z\-]+>\]`, `g`);

/********************************************* MODELS *********************************************/
/**
 * Base store for entire application
 *
 * Contains:
 *     All other web-client stores
 *     Javelin "broadcaster"
 *     Consumable Javelin store
 *     UUID identifying current account (objectId)
 *     App version (appVersion)
 *     "Proxy" properties that call commonly accessed properties in deeper stores
 *
 * On initialization, it also runs a procedure that subscribes to Javelin events
 */
export class RootStore extends BaseStore {
    public static instance: RootStore;

    public hasInitialized = false;
    private hasDoneInitialSort = false;

    /**
     * Should be set to true if unsaved data is present somewhere in the app
     */
    public hasUnsavedData = false;

    /**
     * Sets hasUnsavedData value, which should be true if unsaved data is
     * present somewhere in the app
     *
     * @param {boolean} newHasUnsavedData New value of hasUnsavedData property
     */
    @action
    public setHasUnsavedData = (hasUnsavedData: boolean) => (this.hasUnsavedData = hasUnsavedData);

    /************ JAVELIN MANAGERS ************/
    public syncManager: SyncManagerInstance;
    public javelinManager: JavelinManagerInstance;
    public modelInitStatusStore: ModelInitStatusStore;
    public javelinData: JavelinData;

    // Here for convenience - not actually state
    public notificationManager: NotificationManager = NotificationManager;
    public schedulerManager: SchedulerManager = SchedulerManager;

    /************ OTHER STORES ************/
    // Webclient stores
    @observable
    public languageStore: LanguageStore;

    @observable
    public envVarStore: EnvVarStore;

    @observable
    public queryParamStore: QueryParamStore;

    @observable
    public uiStore: UIStore;

    @observable
    public accountStore: AccountStore;

    @observable
    public recordModelsStore: RecordModelsStore;

    // Javelin stores
    public javelinDataStore: typeof javelinDataStore;
    public jvlnsBroadcaster: Broadcaster;

    /************ COMPUTED & VIRTUAL PROPERTIES ************/
    /**
     * UUID identifying current account
     * Required for transactions to work
     */
    @computed
    public get objectId(): string {
        return this.accountStore.accountId;
    }

    /**
     * Current version of the app {string}
     * @return {string} Current app version
     */
    @computed
    public get appVersion(): string {
        return `${this.envVarStore.clientVersion}`;
    }

    /************ CONSTRUCTION & INITIALIZATION ************/
    /**
     * Gets class props from sessionStorage, assigns default for each not found
     * @return {RootStore} The app data store itself and all child stores
     *                     (available at various appState keys)
     */
    constructor(
        envVars: _EnvVars,
        syncManager?: SyncManagerInstance,
        javelinManager?: JavelinManagerInstance,
        javelinData?: JavelinData
    ) {
        super();

        this.initJavelinEventSubscriptions();

        this.syncManager = syncManager;
        this.javelinManager = javelinManager;
        this.javelinData = javelinData;
        this._setInitialModelValues(envVars);

        this.javelinDataStore = javelinDataStore;
        this.jvlnsBroadcaster = this.javelinDataStore.jvlnsBroadcaster;
        this.modelInitStatusStore = ModelInitStatusStore.getInstance(this);
    }

    /**
     * Sort the records & set the current record (based on the query params)
     */
    sortRecordsSetCurrent = () => {
        // Sort the records
        this.recordModelsStore.recordModels.replace(
            ((this.recordModelsStore.recordModels as any) as RecordModel[]).sort((r1, r2) => {
                const r1Name = typeof r1.firstName === `string` ? r1.firstName.toLowerCase() : `_`;
                const r2Name = typeof r2.firstName === `string` ? r2.firstName.toLowerCase() : `_`;
                return r1Name > r2Name ? 1 : r1Name === r2Name ? 0 : -1;
            })
        );

        // Switch record based on record ID set in query params
        const queryParams = qsParse(window.location.search) as {
            current_record: string;
        } | void;
        if (queryParams && queryParams.current_record) {
            // If record in query param loaded, set it
            if (this.recordModelsStore.hasRecord(queryParams.current_record)) {
                this.recordModelsStore.setActiveRecord(queryParams.current_record);
            }
        }
    };

    /**
     * Reference to Javelin access token
     */
    @computed
    public get accessToken(): string {
        return this.javelinDataStore.token;
    }

    /**
     * Set Javelin access token
     */
    @action
    public setJavelinAccessToken = (token: string) => {
        return this.javelinDataStore.setToken(token);
    };

    /**
     * Initialize Javelin event subscriptions
     * Record load on login; record sort on record load complete; record wipe on logout
     */
    initJavelinEventSubscriptions() {
        /**
         * Wipe all records on logout & de-initialize the store
         */
        javelinDataStore.jvlnsBroadcaster.subscribe(`ev_LOGOUT`, this.logoutDataClear);

        /**
         * Load all records on store initialization, then sort the loaded records
         */
        javelinDataStore.jvlnsBroadcaster.subscribe(`ev_STORE_INITIALIZED`, data => {
            // SOCKET CONNECTION (socket.io)
            if (config.isSocketIoOn && window.location.pathname.includes(`/main`)) {
                const socketClient = io.connect(
                    `${config.urls.syncSocket}?${this.accessToken}`,
                    {transports: [`websocket`]}
                );

                socketClient.on(`sync_account`, socketData => {
                    this.syncManager.handleSocketBroadcast(
                        `sync_account`,
                        JSON.stringify(socketData)
                    );
                });

                socketClient.on(`sync_record`, socketData => {
                    this.syncManager.handleSocketBroadcast(
                        `sync_record`,
                        JSON.stringify(socketData)
                    );
                });
            }

            // Ensure all records grabbed from sync-server
            if (this.javelinDataStore.token && !this.hasInitialized) {
                this.syncManager.refreshRecords();
                this.hasInitialized = true;
            } else {
                this.sortRecordsSetCurrent();
            }
        });

        /**
         * Perform initial sort after login
         * Also updates local isLoggedIn cookie expiration when requests finish
         */
        javelinDataStore.jvlnsBroadcaster.subscribe(`ev_SYNC_COMPLETE`, data => {
            if (this.hasInitialized && !this.hasDoneInitialSort) {
                this.sortRecordsSetCurrent();
                this.hasDoneInitialSort = true;
            }
            // Update expiration of logged in local cookie by re-initializing
            setLoginCookie(true);
        });
    }

    /**
     * Set stores' initial values
     */
    @action
    private _setInitialModelValues(envVars: _EnvVars) {
        this.envVarStore = EnvVarStore.new(envVars);
        this.queryParamStore = QueryParamStore.new();
        this.accountStore = AccountStore.new(this);
        this.uiStore = UIStore.new(this);
        this.recordModelsStore = RecordModelsStore.new(this);
        this.languageStore = LanguageStore.new();
    }

    /********* PROXIES TO COMMON PROPERTIES (Records, RecordModels, & Language) *********/
    /**
     * Get array of observable (via MobX) record data objects from Javelin
     * @return {RecordInstance[]} observable record data objects, directly from Javelin
     */
    @computed
    public get records(): IObservableArray<RecordInstance> {
        return this.javelinDataStore.records;
    }

    public set records(records: IObservableArray<RecordInstance>) {
        this.javelinDataStore.records.replace((records as any) as RecordInstance[]);
    }

    /**
     * Get array of RecordModel store objects, wrapping Javelin Record objects
     * @return {RecordModel[]} Wrapper stores for Javelin Record objects
     */
    public get recordModels() {
        return this.recordModelsStore.recordModels;
    }

    public get activeRecordModel() {
        return this.recordModelsStore.activeRecordModel;
    }

    /**
     * Proxy to setLanguage in language store. Returns actual setLanguage function
     * @type {(lang: string) => string}
     */
    public get setLanguage(): LangSetter {
        return this.languageStore.setLanguage;
    }

    /**
     * Current language abbreviation e.g. `en` or `fr` {string}
     */
    @computed
    public get language(): string {
        return this.languageStore.language;
    }

    /********************* LOGOUT *********************/
    /**
     * Send logout request to server, wipe MobX store data, clear browser storage
     */
    public performLogout = () =>
        new Promise((resolve, reject) => {
            setLoginCookie(false);
            if (!this.accessToken) return resolve(``);
            this.syncManager.logout(
                new RequestCallback({
                    /** Handle logout success */
                    onSuccess: (msg: SuccessInstance) => {
                        if (msg instanceof Error) {
                            return this.wrapLogoutDataClear(resolve, reject, msg);
                        }
                        return this.wrapLogoutDataClear(resolve, reject);
                    },
                    /** Handle logout fail */
                    onError: (err: JvlnsErrorInstance) =>
                        this.wrapLogoutDataClear(resolve, reject, err),
                })
            );
        });

    /**
     * Handler for method to clear data on logout: log errors seen up to this
     * point in logout sequence, handle error & success conditions returned by
     * logoutDataClear function
     */
    private wrapLogoutDataClear = (resolve, reject, err?) => {
        if (err) {
            console.error(
                `RootStore#performLogout: Logout failed with error object 'err':`,
                `\n*** err (below): ***\n`,
                err,
                `\n*** err.message (below): ***\n ${get(err, 'message')}`,
                `\n*** err.stack (below): ***\n ${get(err, 'stack')}`
            );
        }
        return this.logoutDataClear()
            .then(msg => {
                return resolve(``);
            })
            .catch(error => {
                console.error(`RootStore#performLogout: Failed to clear logout data:`, error);
                return reject(error);
            });
    };

    /**
     * Do logout action - remove all records & recordModels
     */
    private logoutDataClear = action((_ev?) => {
        // Erase all records & record models
        while (this.records && this.records.length > 0) {
            this.records.pop();
        }
        while (this.recordModels && this.recordModels.length > 0) {
            this.recordModels.pop();
        }

        this.hasInitialized = false;
        this.accountStore.clearAccountData();
        setLoginCookie(false);

        // Wipe all record model data in localforage
        // Relevant when no RecordModel data available
        return (
            localforage
                .keys()
                .then(this.rmRecModelKeyMatchesFromCache)
                // Erase access token & log out
                .then(() => this.setJavelinAccessToken(``))
        );
    });

    /**
     * Eliminate all RecordModel keys matching an item in the given array
     */
    private rmRecModelKeyMatchesFromCache = (keys: string[]) =>
        Promise.all(
            keys.map(key => (key.match(RM_REGEX) ? localforage.removeItem(key) : Promise.resolve()))
        );
}

/**************************************** FACTORY & EXPORT ****************************************/
/**
 * Factory to build app's state store, returning RootStore class instance
 * i.e. builds the app's core state store
 * @return {RootStore} Instance of RootStore class: i.e. the app's state store
 */
export const appStateFactory = (
    envVars: _EnvVars,
    syncManager?: SyncManagerInstance,
    javelinManager?: JavelinManagerInstance,
    javelinData?: JavelinData
) => {
    RootStore.instance =
        RootStore.instance || new RootStore(envVars, syncManager, javelinManager, javelinData);
    return RootStore.instance;
};

export {RootStore as Store};
